import AsyncStorage from "@react-native-async-storage/async-storage";
import { Query, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

/**
 * Query-key prefixes that must NEVER be written to browser storage.
 *
 * AsyncStorage resolves to localStorage in this (web) build, so without a filter
 * TanStack's default dehydrates every successful query — which put account-holder
 * emails, names and activity, plus the company's MRR / CAC / LTV, into plaintext
 * localStorage, where they survived logout and were readable by anything with
 * access to the browser profile or an XSS on the admin origin.
 *
 * These reads are cheap to re-fetch and must not outlive the session.
 */
const NON_PERSISTABLE_KEY_PREFIXES = ["crm"];

/**
 * Bumped whenever caches already written to storage must be thrown away. The
 * first value retires caches holding article images: they were persisted as
 * Blobs, which JSON turns into `{}`, and a restored `{}` crashed the article
 * editor on reload (`URL.createObjectURL` refuses it).
 */
export const PERSISTED_CACHE_BUSTER = "2026-10-02-no-binary";

/**
 * Data the persister cannot store: it writes JSON, which turns a Blob (or File,
 * or ArrayBuffer) into `{}` — and the restored `{}` is served as if it were
 * the real thing, never refetched under `staleTime: Infinity`.
 */
const isBinary = (data: unknown): boolean =>
  data instanceof Blob || data instanceof ArrayBuffer;

export function isPersistableQuery(query: Query): boolean {
  if (query.state.status !== "success") return false;
  if (isBinary(query.state.data)) return false;
  const root = query.queryKey[0];
  return (
    typeof root !== "string" || !NON_PERSISTABLE_KEY_PREFIXES.includes(root)
  );
}

function AppQueryClient({ children }: any) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        retry: (failureCount, error: any) => {
          // Don't retry on 401 (authentication errors) or 403 (forbidden)
          if (error?.response?.status === 401 || error?.response?.status === 403) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    },
  });

  const asyncStoragePersister = createAsyncStoragePersister({
    storage: AsyncStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        buster: PERSISTED_CACHE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery: isPersistableQuery },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}

export default AppQueryClient;
